import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import debugLogger from '../utils/debugLogger';
import { CheckCircleIcon, XCircleIcon, PencilSquareIcon, TruckIcon, ArchiveBoxXMarkIcon, QuestionMarkCircleIcon, QrCodeIcon, PrinterIcon, ArrowPathIcon, DocumentIcon, ClockIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import CampaignRefundCalculation from './CampaignRefundCalculation';

const statusConfig = {
    pending_approval: { text: 'Onay Bekliyor', color: 'yellow', icon: QuestionMarkCircleIcon },
    approved: { text: 'Müşterinin kargoya vermesi bekleniyor', color: 'blue', icon: CheckCircleIcon },
    customer_shipped: { text: 'Müşteri kargoya verdi', color: 'purple', icon: TruckIcon },
    in_transit: { text: 'Yolda', color: 'purple', icon: TruckIcon },
    received: { text: 'Mağazada alındı', color: 'indigo', icon: CheckCircleIcon },
    under_review: { text: 'İncelemede', color: 'orange', icon: ClockIcon },
    review_completed: { text: 'İnceleme tamamlandı', color: 'blue', icon: CheckCircleIcon },
    refund_approved: { text: 'İade onaylandı', color: 'green', icon: CheckCircleIcon },
    refund_completed: { text: 'İade tamamlandı', color: 'emerald', icon: CheckCircleIcon },
    rejected: { text: 'Reddedildi', color: 'red', icon: XCircleIcon },
    completed: { text: 'Tamamlandı', color: 'green', icon: CheckCircleIcon },
    cancelled: { text: 'İptal edildi', color: 'red', icon: XCircleIcon },
};

// Kargonomi durum konfigürasyonu
const kargonomiStatusConfig = {
    return_label_created: { text: 'Etiket Oluşturuldu', color: 'blue' },
    return_draft: { text: 'Taslak', color: 'gray' },
    return_confirmed: { text: 'Onaylandı', color: 'green' },
    return_in_transit: { text: 'Yolda', color: 'purple' },
    return_delivered: { text: 'Teslim Edildi', color: 'green' },
    return_cancelled: { text: 'İptal Edildi', color: 'red' },
    return_failed: { text: 'Başarısız', color: 'red' },
    return_unknown: { text: 'Bilinmiyor', color: 'gray' }
};

// İletişim Geçmişi Komponenti
const CommunicationHistory = ({ returnId }) => {
    const [communications, setCommunications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAddingNote, setIsAddingNote] = useState(false);
    const [newNote, setNewNote] = useState({
        type: 'note',
        content: '',
        contact_method: 'email'
    });
    const { token } = useAuth();

    const fetchCommunications = useCallback(async () => {
        if (!returnId || !token) return;
        setLoading(true);
        try {
            const data = await api.get(`/api/returns/${returnId}/communications`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCommunications(data || []);
        } catch (error) {
            console.error('İletişim geçmişi getirilemedi:', error);
            setCommunications([]);
        } finally {
            setLoading(false);
        }
    }, [returnId, token]);

    useEffect(() => {
        fetchCommunications();
    }, [fetchCommunications]);

    const handleAddCommunication = async (e) => {
        e.preventDefault();
        if (!newNote.content.trim()) {
            toast.warn('Lütfen not içeriğini girin.');
            return;
        }

        setLoading(true);
        try {
            await api.post(`/api/returns/${returnId}/communications`, newNote, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('İletişim notu eklendi.');
            setNewNote({ type: 'note', content: '', contact_method: 'email' });
            setIsAddingNote(false);
            fetchCommunications();
        } catch (error) {
            toast.error(error.response?.data?.error || 'İletişim notu eklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'email': return '📧';
            case 'phone': return '📞';
            case 'whatsapp': return '💬';
            case 'note': return '📝';
            case 'payment': return '💰';
            default: return '📋';
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'email': return 'bg-blue-100 text-blue-800';
            case 'phone': return 'bg-green-100 text-green-800';
            case 'whatsapp': return 'bg-green-100 text-green-800';
            case 'note': return 'bg-gray-100 text-gray-800';
            case 'payment': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-4">
                <h6 className="font-semibold text-gray-800">İletişim Geçmişi</h6>
                <button
                    onClick={() => setIsAddingNote(!isAddingNote)}
                    className="px-3 py-1 bg-brand-green text-white rounded-lg text-sm font-medium hover:bg-brand-green-light transition-colors"
                >
                    {isAddingNote ? 'İptal' : '+ Not Ekle'}
                </button>
            </div>

            {/* Yeni Not Ekleme Formu */}
            {isAddingNote && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
                    <form onSubmit={handleAddCommunication} className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">İletişim Türü</label>
                                <select
                                    value={newNote.type}
                                    onChange={(e) => setNewNote({ ...newNote, type: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green text-sm"
                                >
                                    <option value="note">Genel Not</option>
                                    <option value="email">Email İletişimi</option>
                                    <option value="phone">Telefon Görüşmesi</option>
                                    <option value="whatsapp">WhatsApp</option>
                                    <option value="payment">Ödeme Notu</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">İletişim Yöntemi</label>
                                <select
                                    value={newNote.contact_method}
                                    onChange={(e) => setNewNote({ ...newNote, contact_method: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green text-sm"
                                >
                                    <option value="email">Email</option>
                                    <option value="phone">Telefon</option>
                                    <option value="whatsapp">WhatsApp</option>
                                    <option value="in_person">Yüz Yüze</option>
                                    <option value="other">Diğer</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">İçerik</label>
                            <textarea
                                value={newNote.content}
                                onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                                rows={3}
                                placeholder="İletişim detaylarını, konuşulan konuları veya notları buraya yazın..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green text-sm"
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-brand-green text-white py-2 rounded-lg font-medium hover:bg-brand-green-light disabled:bg-gray-400 transition-colors text-sm"
                            >
                                {loading ? 'Ekleniyor...' : 'Not Ekle'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsAddingNote(false)}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors text-sm"
                            >
                                İptal
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* İletişim Geçmişi Listesi */}
            <div className="space-y-3 max-h-60 overflow-y-auto">
                {loading && communications.length === 0 ? (
                    <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand-green border-t-transparent mx-auto"></div>
                    </div>
                ) : communications.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">
                        Henüz iletişim kaydı yok.
                    </div>
                ) : (
                    communications.map((comm, index) => (
                        <div key={comm.id || index} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-lg">{getTypeIcon(comm.type)}</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(comm.type)}`}>
                                        {comm.type === 'email' ? 'Email' :
                                            comm.type === 'phone' ? 'Telefon' :
                                                comm.type === 'whatsapp' ? 'WhatsApp' :
                                                    comm.type === 'payment' ? 'Ödeme' : 'Not'}
                                    </span>
                                    {comm.contact_method && (
                                        <span className="text-xs text-gray-500">
                                            via {comm.contact_method}
                                        </span>
                                    )}
                                </div>
                                <span className="text-xs text-gray-500">
                                    {new Date(comm.created_at).toLocaleString('tr-TR')}
                                </span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{comm.content}</p>
                            {comm.admin_name && (
                                <p className="text-xs text-gray-500 mt-1">
                                    - {comm.admin_name}
                                </p>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export const ReturnActionModal = ({ returnRequest, closeModal, onActionSuccess, onUpdateTrackingInfo, fetchRealTrackingCode, formatTrackingCode }) => {
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState({ returnAddresses: [], shippingCompanies: [] });
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [kargonomiLoading, setKargonomiLoading] = useState(false);
    const [showKargonomiLabel, setShowKargonomiLabel] = useState(false);

    // Kargonomi kargo firmaları için yeni state'ler
    const [kargonomiProviders, setKargonomiProviders] = useState([]);
    const [loadingKargonomiProviders, setLoadingKargonomiProviders] = useState(false);
    const [selectedKargonomiProvider, setSelectedKargonomiProvider] = useState(null);
    const [isReturnReasonExpanded, setIsReturnReasonExpanded] = useState(false);

    const { token } = useAuth();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const returnSettings = await api.get('/api/settings/return-settings', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSettings({
                    returnAddresses: returnSettings.returnAddresses || [],
                    shippingCompanies: returnSettings.shippingCompanies || [],
                    defaultShippingCompany: returnSettings.defaultShippingCompany || ''
                });
                // Varsayılan olarak .env adresini (liste basindaki) sec
                const envDefault = (returnSettings.returnAddresses || [])[0];
                if (envDefault) setSelectedAddress(envDefault);
            } catch (err) {
                toast.error('İade ayarları getirilemedi.');
            }
        };
        fetchSettings();
    }, [token]);

    // Kargonomi'den kargo firmalarını çek
    const fetchKargonomiProviders = async () => {
        if (!selectedAddress || !returnRequest.orders?.shipping_address) {
            console.log('[ReturnModal] Adres bilgileri eksik, kargo firmaları çekilemiyor');
            return;
        }

        setLoadingKargonomiProviders(true);
        try {
            console.log('[ReturnModal] Kargonomi kargo firmaları çekiliyor...');

            // Müşteri adres bilgilerini parse et
            let customerAddress = {};
            try {
                const shippingAddress = JSON.parse(returnRequest.orders.shipping_address);
                customerAddress = {
                    city: shippingAddress.city || 'İstanbul',
                    district: shippingAddress.district || 'Kadıköy'
                };
            } catch (parseError) {
                console.warn('[ReturnModal] Müşteri adres bilgisi parse edilemedi, varsayılan kullanılıyor');
                customerAddress = { city: 'İstanbul', district: 'Kadıköy' };
            }

            // İade kargo firmalarını kontrol et
            const response = await api.post('/api/kargonomi/check-return-providers', {
                customerAddress: customerAddress,
                returnAddress: selectedAddress,
                desi: 1 // Varsayılan desi
            });

            console.log('[ReturnModal] Kargonomi yanıtı:', response);

            if (response.status === 'success' && response.providers && response.providers.length > 0) {
                setKargonomiProviders(response.providers);

                // En ucuz firmayı varsayılan olarak seç
                if (response.cheapest) {
                    setSelectedKargonomiProvider(response.cheapest);
                    setSelectedCompany(response.cheapest.name);
                }

                console.log(`[ReturnModal] ✅ ${response.providers.length} kargo firması bulundu`);
                toast.success(`${response.providers.length} kargo seçeneği bulundu!`, {
                    position: "top-center",
                    autoClose: 3000,
                });
            } else {
                console.warn('[ReturnModal] Kargonomi\'den kargo firması bulunamadı, varsayılan listeye dönülüyor');
                setKargonomiProviders([]);
                toast.warning('Kargonomi\'den kargo firması bulunamadı. Varsayılan liste kullanılıyor.', {
                    position: "top-center",
                    autoClose: 5000,
                });
            }
        } catch (error) {
            console.error('[ReturnModal] Kargonomi kargo firmaları çekilirken hata:', error);
            setKargonomiProviders([]);
            toast.error('Kargo firmaları yüklenemedi. Varsayılan liste kullanılıyor.', {
                position: "top-center",
                autoClose: 5000,
            });
        } finally {
            setLoadingKargonomiProviders(false);
        }
    };

    // Adres seçildiğinde kargo firmalarını otomatik çek
    useEffect(() => {
        if (selectedAddress && returnRequest.orders?.shipping_address) {
            fetchKargonomiProviders();
        }
    }, [selectedAddress, returnRequest.orders?.shipping_address]);

    const handleUpdate = async (status) => {
        setLoading(true);
        try {
            console.log('[ReturnModal] 🚀 İADE GÜNCELLEME BAŞLADI');
            console.log('[ReturnModal] 📋 Güncelleme detayları:', {
                status,
                returnRequestId: returnRequest.id,
                selectedAddress: selectedAddress ? 'Mevcut' : 'Yok',
                selectedCompany: selectedCompany ? 'Mevcut' : 'Yok',
                adminNotes: adminNotes ? 'Mevcut' : 'Yok',
                selectedKargonomiProvider: selectedKargonomiProvider ? 'Mevcut' : 'Yok'
            });
            console.log('[ReturnModal] 🔍 Seçilen adres detayları:', selectedAddress);
            console.log('[ReturnModal] 🚚 Seçilen kargo firması:', selectedCompany);
            console.log('[ReturnModal] 💰 Kargonomi sağlayıcı:', selectedKargonomiProvider);
            if (status === 'approved') {
                if (!selectedAddress || !selectedCompany) {
                    toast.warn('Lütfen iade adresi ve kargo firması seçin.');
                    setLoading(false);
                    return;
                }

                // Kargonomi kargo firması seçilmişse, o bilgileri kullan
                let shippingData = {
                    return_address: selectedAddress,
                    shipping_company_for_return: selectedCompany,
                    admin_notes: adminNotes
                };

                // Eğer Kargonomi'den seçilen bir kargo firması varsa, o bilgileri ekle
                if (selectedKargonomiProvider) {
                    shippingData.kargonomi_provider = {
                        id: selectedKargonomiProvider.id,
                        name: selectedKargonomiProvider.name,
                        price: selectedKargonomiProvider.price,
                        estimatedDays: selectedKargonomiProvider.estimatedDays
                    };
                    console.log('[ReturnModal] Kargonomi kargo firması bilgileri eklendi:', selectedKargonomiProvider);
                }

                console.log('[ReturnModal] 📤 API isteği gönderiliyor:', {
                    url: `/api/admin/returns/${returnRequest.id}/approve`,
                    method: 'PUT',
                    data: shippingData
                });

                // Yeni approve endpoint'ini kullan
                const response = await api.put(`/api/admin/returns/${returnRequest.id}/approve`, shippingData, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                console.log('[ReturnModal] ✅ API yanıtı başarılı:', response.data);
                toast.success('İade talebi başarıyla onaylandı ve kargo etiketi oluşturuldu.');
            } else if (status === 'rejected') {
                // Yeni reject endpoint'ini kullan
                await api.put(`/api/admin/returns/${returnRequest.id}/reject`, {
                    rejection_reason: adminNotes || 'İade talebi reddedildi',
                    admin_notes: adminNotes
                }, { headers: { Authorization: `Bearer ${token}` } });

                toast.success('İade talebi reddedildi.');
            } else {
                // Genel durum güncelleme
                await api.put(`/api/admin/returns/${returnRequest.id}/update-status`, {
                    status: status,
                    admin_notes: adminNotes
                }, { headers: { Authorization: `Bearer ${token}` } });

                toast.success(`İade durumu güncellendi: ${statusConfig[status].text}`);
            }

            onActionSuccess();
            closeModal();
        } catch (err) {
            console.error('[ReturnModal] ❌ İADE GÜNCELLEME HATASI:', {
                message: err.message,
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
                url: err.config?.url,
                method: err.config?.method
            });
            toast.error(err.response?.data?.error || "İade talebi güncellenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const handleCargoReceived = async () => {
        setLoading(true);
        try {
            // Yeni endpoint ile manuel status güncelleme
            await api.put(`/api/admin/returns/${returnRequest.id}/status`, {
                status: 'received',
                admin_notes: 'Manuel durum güncellemesi - Kargo alındı (Test)'
            }, { headers: { Authorization: `Bearer ${token}` } });

            toast.success('✅ Kargo alındı olarak işaretlendi!');
            onActionSuccess();
            closeModal();
        } catch (err) {
            console.error('Manuel status güncelleme hatası:', err);
            toast.error(err.response?.data?.error || "Kargo alındı işaretlenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Manuel Status Güncelleme (Test amaçlı)
    const handleManualStatusUpdate = async (newStatus) => {
        setLoading(true);
        try {
            const statusLabels = {
                'received': 'Teslim Alındı',
                'under_review': 'İncelemede',
                'review_completed': 'İnceleme Tamamlandı',
                'completed': 'Tamamlandı'
            };

            await api.put(`/api/admin/returns/${returnRequest.id}/status`, {
                status: newStatus,
                admin_notes: `Manuel durum güncellemesi: ${statusLabels[newStatus] || newStatus}`
            }, { headers: { Authorization: `Bearer ${token}` } });

            toast.success(`✅ Durum "${statusLabels[newStatus] || newStatus}" olarak güncellendi!`);
            onActionSuccess();
            closeModal();
        } catch (err) {
            console.error('Manuel status güncelleme hatası:', err);
            toast.error(err.response?.data?.error || 'Durum güncellenirken hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleFinalDecision = async (decision) => {
        let finalNote = adminNotes.trim();

        // Eğer yönetici notu boşsa ve işlem reddediliyorsa uyarı ver
        if (decision === 'rejected' && !finalNote) {
            toast.warn('Lütfen red gerekçenizi yazın.');
            return;
        }

        setLoading(true);
        try {
            if (decision === 'completed') {
                // İadeyi tamamla ve otomatik geri ödeme yap
                console.log('[ReturnModal] 💰 İade tamamlama ve geri ödeme işlemi başlatılıyor...');
                const response = await api.post(`/api/admin/returns/${returnRequest.id}/complete-return`, {
                    admin_notes: finalNote,
                    refund_method: 'automatic' // Varsayılan olarak otomatik iade
                }, { headers: { Authorization: `Bearer ${token}` } });

                if (response.success) {
                    toast.success('İade tamamlandı ve geri ödeme işlemi başlatıldı.');
                    if (response.refund) {
                        console.log('[ReturnModal] ✅ Geri ödeme detayları:', response.refund);
                    }
                } else {
                    toast.warn('İade tamamlandı ancak geri ödeme işleminde bir uyarı oluştu.');
                }
            } else {
                // Diğer durum güncellemeleri (reddetme vb.)
                await api.put(`/api/admin/returns/${returnRequest.id}/update-status`, {
                    status: decision,
                    admin_notes: finalNote
                }, { headers: { Authorization: `Bearer ${token}` } });

                toast.success(`İade ${decision === 'rejected' ? 'reddedildi' : 'durumu güncellendi'}.`);
            }

            onActionSuccess();
            closeModal();
        } catch (err) {
            console.error('[ReturnModal] Final karar hatası:', err);
            toast.error(err.response?.data?.error || "Final karar verilirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentCompleted = async () => {
        setLoading(true);
        try {
            await api.put(`/api/returns/admin/${returnRequest.id}/payment-completed`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Ödeme tamamlandı olarak işaretlendi.');
            onActionSuccess();
            closeModal();
        } catch (err) {
            toast.error(err.response?.data?.error || "Ödeme durumu güncellenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Kargonomi durumu güncelleme
    const handleUpdateKargonomiStatus = async () => {
        setKargonomiLoading(true);
        try {
            const response = await api.post(`/api/returns/admin/${returnRequest.id}/update-kargonomi-status`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Kargonomi durumu güncellendi.');
            onActionSuccess();
        } catch (error) {
            console.error('Kargonomi durum güncelleme hatası:', error);
            toast.error('Kargonomi durumu güncellenirken bir hata oluştu.');
        } finally {
            setKargonomiLoading(false);
        }
    };

    // Kargonomi etiketini yeniden oluştur
    const handleRecreateKargonomiLabel = async () => {
        if (!selectedAddress || !selectedCompany) {
            toast.warn('Lütfen iade adresi ve kargo firması seçin.');
            return;
        }

        setKargonomiLoading(true);
        try {
            const response = await api.post(`/api/admin/returns/${returnRequest.id}/create-shipment`, {
                return_address: selectedAddress,
                shipping_company_for_return: selectedCompany
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('İade kargo etiketi başarıyla oluşturuldu.');
            onActionSuccess();
        } catch (error) {
            console.error('Kargonomi etiket oluşturma hatası:', error);
            toast.error('Kargo etiketi oluşturulurken bir hata oluştu.');
        } finally {
            setKargonomiLoading(false);
        }
    };

    // Kargonomi barkod numarasını göster
    const handleShowKargonomiLabel = async () => {
        try {
            console.log('[ReturnModal] Barkod numarası alınıyor...');
            console.log('[ReturnModal] İade ID:', returnRequest.id);

            const response = await api.get(`/api/returns/admin/${returnRequest.id}/barcode`);

            console.log('[ReturnModal] API Response:', response);

            if (response && response.success) {
                const barcodeNumber = response.barcodeNumber;
                console.log('[ReturnModal] Barkod numarası başarıyla alındı:', barcodeNumber);

                toast.success(`Barkod Numarası: ${barcodeNumber}`, {
                    position: "top-center",
                    autoClose: 5000,
                });

                // Barkod numarasını panoya kopyala
                if (navigator.clipboard) {
                    navigator.clipboard.writeText(barcodeNumber);
                    toast.info('Barkod numarası panoya kopyalandı!', {
                        position: "top-center",
                        autoClose: 3000,
                    });
                }
            } else {
                console.log('[ReturnModal] Response success false veya data yok');
                console.log('[ReturnModal] Response:', response);
                toast.error('Barkod numarası alınamadı.');
            }

        } catch (error) {
            console.error('Kargonomi barkod numarası alma hatası:', error);
            console.error('Error response:', error.response);
            toast.error('Barkod numarası alınamadı.');
        }
    };

    const renderContent = () => {
        switch (returnRequest.status) {
            case 'pending_approval':
                return (
                    <>
                        <p className="font-semibold text-sm mb-2">İadeyi Onaylama İşlemi:</p>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                            <p className="text-sm text-blue-700">
                                <strong>🚀 Yeni:</strong> İade onaylandığında Kargonomi ile otomatik kargo etiketi oluşturulacak!
                            </p>
                        </div>
                        <div className="space-y-6">
                            <div>
                                <label htmlFor="returnAddress" className="block text-sm font-medium text-gray-700 mb-2">İade Adresi Seçin</label>
                                <select
                                    id="returnAddress"
                                    onChange={(e) => setSelectedAddress(JSON.parse(e.target.value))}
                                    className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-green focus:ring-brand-green transition-colors"
                                    defaultValue=""
                                >
                                    <option value="">Adres seçiniz...</option>
                                    {settings.returnAddresses.map((addr, idx) => (
                                        <option key={idx} value={JSON.stringify(addr)}>
                                            {addr.title} - {addr.city}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="shippingCompany" className="block text-sm font-medium text-gray-700 mb-2">
                                    Müşterinin Kullanacağı Kargo Firması
                                    {kargonomiProviders.length > 0 && (
                                        <span className="ml-2 text-xs text-green-600 font-normal">
                                            (Kargonomi'den {kargonomiProviders.length} seçenek)
                                        </span>
                                    )}
                                </label>

                                {loadingKargonomiProviders ? (
                                    <div className="flex items-center justify-center p-3 border-2 border-gray-200 rounded-xl bg-gray-50">
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-green border-t-transparent mr-2"></div>
                                        <span className="text-gray-600 text-sm">Kargo firmaları yükleniyor...</span>
                                    </div>
                                ) : kargonomiProviders.length > 0 ? (
                                    <select
                                        id="shippingCompany"
                                        value={selectedCompany || ''}
                                        onChange={(e) => {
                                            const selected = kargonomiProviders.find(p => p.name === e.target.value);
                                            setSelectedCompany(e.target.value);
                                            setSelectedKargonomiProvider(selected);
                                            console.log('[ReturnModal] Kargo firması seçildi:', selected);
                                        }}
                                        className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-green focus:ring-brand-green transition-colors"
                                    >
                                        <option value="">Kargo firması seçin...</option>
                                        {kargonomiProviders.map(provider => (
                                            <option key={provider.id || provider.name} value={provider.name}>
                                                {provider.name} - ₺{provider.price?.toFixed(2) || '0.00'}
                                                {provider.estimatedDays && ` (${provider.estimatedDays} gün)`}
                                                {provider.isCheapest && ' - En Uygun'}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <select
                                        id="shippingCompany"
                                        value={selectedCompany || settings.defaultShippingCompany}
                                        onChange={(e) => setSelectedCompany(e.target.value)}
                                        className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-green focus:ring-brand-green transition-colors"
                                    >
                                        <option value="">Kargo firması seçin...</option>
                                        {settings.shippingCompanies.map(comp => (
                                            <option key={comp} value={comp}>
                                                {comp} {comp === settings.defaultShippingCompany ? '(Varsayılan)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                )}

                                {kargonomiProviders.length === 0 && !loadingKargonomiProviders && (
                                    <div className="mt-2 text-xs text-gray-500">
                                        Kargonomi'den kargo firması bulunamadı. Varsayılan liste kullanılıyor.
                                    </div>
                                )}
                            </div>
                            <div className="mt-4">
                                <label htmlFor="adminNotes" className="block text-sm font-medium text-gray-700 mb-2">Admin Notları (İsteğe bağlı)</label>
                                <textarea
                                    id="adminNotes"
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    rows={2}
                                    className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-green focus:ring-brand-green transition-colors"
                                    placeholder="İade ile ilgili notlarınızı yazabilirsiniz..."
                                ></textarea>
                            </div>
                        </div>
                        <div className="flex justify-center space-x-4 mt-6">
                            <button onClick={() => handleUpdate('approved')} disabled={loading} className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"><CheckCircleIcon className="h-5 w-5 mr-2" /> Onayla & Kargo Etiketi Oluştur</button>
                            <button onClick={() => handleUpdate('rejected')} disabled={loading} className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"><XCircleIcon className="h-5 w-5 mr-2" /> Reddet</button>
                        </div>
                    </>
                );
            case 'approved':
                return (
                    <>
                        <p className="font-semibold text-sm mb-2">Kargonomi Kargo Yönetimi:</p>

                        {/* Kargonomi Bilgileri */}
                        {returnRequest.kargonomi_shipment_id ? (
                            <div className="space-y-4">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <h6 className="font-semibold text-green-800 mb-2">✅ Kargonomi Etiketi Oluşturuldu</h6>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        {/* Takip numarası güncelleme butonu */}
                                        {returnRequest.kargonomi_shipment_id && (
                                            <div className="col-span-full mt-2">
                                                <button
                                                    onClick={() => onUpdateTrackingInfo(returnRequest.id)}
                                                    disabled={loading}
                                                    className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                                                >
                                                    <span>📱</span>
                                                    <span>Takip Bilgilerini Güncelle</span>
                                                </button>
                                            </div>
                                        )}
                                        {returnRequest.kargonomi_shipping_cost && (
                                            <div>
                                                <span className="text-gray-600">Kargo Ücreti:</span>
                                                <p className="font-medium">{returnRequest.kargonomi_shipping_cost} TL</p>
                                            </div>
                                        )}
                                        {returnRequest.kargonomi_status && (
                                            <div>
                                                <span className="text-gray-600">Kargo Durumu:</span>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${kargonomiStatusConfig[returnRequest.kargonomi_status]?.color === 'green' ? 'bg-green-100 text-green-800' :
                                                    kargonomiStatusConfig[returnRequest.kargonomi_status]?.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                                                        kargonomiStatusConfig[returnRequest.kargonomi_status]?.color === 'purple' ? 'bg-purple-100 text-purple-800' :
                                                            kargonomiStatusConfig[returnRequest.kargonomi_status]?.color === 'red' ? 'bg-red-100 text-red-800' :
                                                                'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {kargonomiStatusConfig[returnRequest.kargonomi_status]?.text || returnRequest.kargonomi_status}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Kargonomi İşlem Butonları */}
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={handleUpdateKargonomiStatus}
                                        disabled={kargonomiLoading}
                                        className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-gray-400"
                                    >
                                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                                        {kargonomiLoading ? 'Güncelleniyor...' : 'Durumu Güncelle'}
                                    </button>

                                    <button
                                        onClick={handleShowKargonomiLabel}
                                        className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                                    >
                                        <QrCodeIcon className="h-4 w-4 mr-1" />
                                        Barkod Numarası
                                    </button>

                                    <button
                                        onClick={handleRecreateKargonomiLabel}
                                        disabled={kargonomiLoading || !selectedAddress}
                                        className="flex items-center px-3 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:bg-gray-400"
                                    >
                                        <PrinterIcon className="h-4 w-4 mr-1" />
                                        {kargonomiLoading ? 'Oluşturuluyor...' : 'Yeniden Oluştur'}
                                    </button>
                                </div>

                                {/* Etiket Yeniden Oluşturma için Adres Seçimi */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Yeniden Oluşturma için İade Adresi:</label>
                                    <select
                                        onChange={(e) => setSelectedAddress(JSON.parse(e.target.value))}
                                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-green focus:ring-brand-green text-sm"
                                        defaultValue=""
                                    >
                                        <option value="">Adres seçiniz...</option>
                                        {settings.returnAddresses.map((addr, idx) => (
                                            <option key={idx} value={JSON.stringify(addr)}>
                                                {addr.title} - {addr.city}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Manuel Status Güncelleme Bölümü (Test için) */}
                                <div className="mt-6 pt-6 border-t border-gray-300">
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                        <h6 className="font-semibold text-yellow-800 mb-2">
                                            🔧 Manuel Durum Güncelleme (Test Amaçlı)
                                        </h6>
                                        <p className="text-yellow-700 text-xs mb-4">
                                            ⚠️ Bu butonlar test için kullanılır. Gerçek kullanımda kargo durumu otomatik takip edilir.
                                        </p>

                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                onClick={() => handleManualStatusUpdate('received')}
                                                disabled={loading}
                                                className="flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                                            >
                                                <span>✅</span>
                                                <span>Teslim Alındı</span>
                                            </button>

                                            <button
                                                onClick={() => handleManualStatusUpdate('under_review')}
                                                disabled={loading}
                                                className="flex items-center justify-center px-3 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50 transition-colors"
                                            >
                                                <span>🔍</span>
                                                <span>İncelemede</span>
                                            </button>

                                            <button
                                                onClick={() => handleManualStatusUpdate('review_completed')}
                                                disabled={loading}
                                                className="flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                            >
                                                <span>✔️</span>
                                                <span>İnceleme Tamam</span>
                                            </button>

                                            <button
                                                onClick={() => handleManualStatusUpdate('completed')}
                                                disabled={loading}
                                                className="flex items-center justify-center px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                                            >
                                                <span>🎉</span>
                                                <span>İade Tamamla</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : returnRequest.kargonomi_error ? (
                            <div className="space-y-4">
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <h6 className="font-semibold text-red-800 mb-2">❌ Kargonomi Hatası</h6>
                                    <p className="text-red-700 text-sm mb-3">{returnRequest.kargonomi_error}</p>
                                    <button
                                        onClick={handleRecreateKargonomiLabel}
                                        disabled={kargonomiLoading || !selectedAddress}
                                        className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:bg-gray-400"
                                    >
                                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                                        {kargonomiLoading ? 'Yeniden Deneniyor...' : 'Yeniden Dene'}
                                    </button>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">İade Adresi Seçin:</label>
                                    <select
                                        onChange={(e) => setSelectedAddress(JSON.parse(e.target.value))}
                                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-green focus:ring-brand-green text-sm"
                                        defaultValue=""
                                    >
                                        <option value="">Adres seçiniz...</option>
                                        {settings.returnAddresses.map((addr, idx) => (
                                            <option key={idx} value={JSON.stringify(addr)}>
                                                {addr.title} - {addr.city}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <p className="text-yellow-800 text-sm">⏳ Kargonomi etiketi oluşturulmamış. Lütfen iade onayını tekrar yapın.</p>
                            </div>
                        )}

                        <p className="text-xs text-gray-500 mt-4">Müşteri ürünü kargoya verdiğinde otomatik olarak takip edilecek.</p>
                    </>
                );
            case 'shipped':
                return (
                    <>
                        <p className="font-semibold text-sm mb-2">Kargo Durumu:</p>
                        <p className="text-xs text-gray-500 mb-4">Müşteri ürünü kargoya verdi. Kargo elinize ulaştığında "Kargo Alındı" butonuna basın.</p>
                        <div className="flex justify-center">
                            <button onClick={() => handleCargoReceived()} disabled={loading} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
                                📦 Kargo Alındı
                            </button>
                        </div>
                    </>
                );
            case 'received':
                return (
                    <>
                        <p className="font-semibold text-sm mb-2">Nihai Karar:</p>
                        <p className="text-xs text-gray-500 mb-4">Ürünleri inceleyip iadeyi onaylayın veya reddedin.</p>
                        <div className="mb-4">
                            <label htmlFor="finalDecisionNotes" className="block text-sm font-medium text-gray-700">Karar Gerekçesi (Müşteriye gösterilecek)</label>
                            <div className="mt-1 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-700">
                                    <strong>Not:</strong> İade onayı için boş bırakırsanız, müşteriye otomatik olarak ödeme bilgilerini isteme mesajı gönderilir.
                                </p>
                            </div>
                            <textarea
                                id="finalDecisionNotes"
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                                rows={3}
                                className="mt-1 block w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-green focus:ring-brand-green"
                                placeholder="İsteğe bağlı: Ek açıklama yazabilirsiniz..."
                            ></textarea>
                        </div>
                        <div className="flex justify-center space-x-4">
                            <button onClick={() => handleFinalDecision('completed')} disabled={loading} className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700">
                                <CheckCircleIcon className="h-5 w-5 mr-2" /> İadeyi Onayla
                            </button>
                            <button onClick={() => handleFinalDecision('rejected')} disabled={loading} className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700">
                                <XCircleIcon className="h-5 w-5 mr-2" /> İadeyi Reddet
                            </button>
                        </div>
                    </>
                );
            case 'completed':
                return (
                    <>
                        <p className="font-semibold text-sm mb-2">Ödeme Durumu:</p>
                        {returnRequest.payment_info ? (
                            <div className="space-y-4">
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                    <h6 className="font-semibold text-blue-800 mb-2">Müşteri Ödeme Bilgileri:</h6>
                                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                                        <p className="text-blue-800 whitespace-pre-wrap">{returnRequest.payment_info}</p>
                                    </div>
                                </div>

                                {returnRequest.payment_status !== 'paid' ? (
                                    <div className="space-y-3">
                                        <div className="flex justify-center">
                                            <button
                                                onClick={() => handlePaymentCompleted()}
                                                disabled={loading}
                                                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center"
                                            >
                                                💰 Ödeme Yapıldı Olarak İşaretle
                                            </button>
                                        </div>
                                        <CommunicationHistory returnId={returnRequest.id} />
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                                            <p className="text-green-800 font-semibold">✅ Ödeme Tamamlandı</p>
                                            <p className="text-sm text-green-600">Bu iade işlemi tamamen tamamlanmıştır.</p>
                                        </div>
                                        <CommunicationHistory returnId={returnRequest.id} />
                                    </div>
                                )}
                            </div>
                        ) : (returnRequest.contact_admin_requested || returnRequest.admin_notes?.includes('İLETİŞİM TALEBİ')) ? (
                            <div className="space-y-4">
                                <div className="text-center p-4 bg-orange-50 border border-orange-200 rounded-lg">
                                    <p className="text-orange-800 font-semibold">📞 Müşteri İletişim Talep Etti</p>
                                    <p className="text-sm text-orange-600">Ödeme işlemi için müşteriyle iletişime geçin.</p>
                                </div>
                                <div className="flex justify-center">
                                    <button
                                        onClick={() => handlePaymentCompleted()}
                                        disabled={loading}
                                        className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center"
                                    >
                                        💰 Ödeme Yapıldı Olarak İşaretle
                                    </button>
                                </div>
                                <CommunicationHistory returnId={returnRequest.id} />
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-yellow-800">⏳ Müşterinin ödeme bilgilerini girmesi bekleniyor.</p>
                                </div>
                                <CommunicationHistory returnId={returnRequest.id} />
                            </div>
                        )}
                    </>
                );
            default:
                return <p className="text-center text-gray-600">Bu aşamada yapılacak bir işlem bulunmuyor.</p>;
        }
    };

    return (
        <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full mx-auto border border-gray-100 overflow-hidden max-h-[90vh] flex flex-col">
            {/* Modern Header */}
            <div className="bg-gradient-to-r from-brand-green to-brand-green-light p-6 text-white flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-2xl font-bold">İade Talebi Yönetimi</h3>
                        <p className="text-brand-green-light opacity-90">#{returnRequest.id.slice(-6)} - MINELA-{returnRequest.orders.order_number}</p>
                    </div>
                    <div className="text-right">
                        <span className={`px-4 py-2 rounded-full text-sm font-bold border-2 border-white/30 ${statusConfig[returnRequest.status]?.color === 'green' ? 'bg-green-500/20 text-green-100' :
                            statusConfig[returnRequest.status]?.color === 'red' ? 'bg-red-500/20 text-red-100' :
                                statusConfig[returnRequest.status]?.color === 'blue' ? 'bg-blue-500/20 text-blue-100' :
                                    statusConfig[returnRequest.status]?.color === 'purple' ? 'bg-purple-500/20 text-purple-100' :
                                        statusConfig[returnRequest.status]?.color === 'indigo' ? 'bg-indigo-500/20 text-indigo-100' :
                                            'bg-yellow-500/20 text-yellow-100'
                            }`}>
                            {statusConfig[returnRequest.status]?.text}
                        </span>
                    </div>
                </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto">
                {/* Müşteri ve Sipariş Bilgileri */}
                <div className="p-6 bg-gray-50 border-b">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                            <div className="flex items-center mb-2">
                                <div className="w-8 h-8 bg-brand-green rounded-lg flex items-center justify-center mr-3">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                <h4 className="font-semibold text-gray-800">Müşteri Bilgileri</h4>
                            </div>
                            <p className="font-medium text-gray-900">{returnRequest.profiles?.full_name || 'Bilinmeyen Kullanıcı'}</p>
                            <p className="text-sm text-gray-600">{returnRequest.profiles?.email || 'Email bilgisi yok'}</p>
                        </div>

                        <div className="bg-white rounded-xl p-4 shadow-sm">
                            <div className="flex items-center mb-2">
                                <div className="w-8 h-8 bg-brand-green rounded-lg flex items-center justify-center mr-3">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M8 11v6h8v-6M8 11H6a2 2 0 00-2 2v6a2 2 0 002 2h12a2 2 0 002-2v-6a2 2 0 00-2-2h-2" />
                                    </svg>
                                </div>
                                <h4 className="font-semibold text-gray-800">Sipariş Bilgileri</h4>
                            </div>
                            <p className="font-medium text-gray-900">MINELA-{returnRequest.orders.order_number}</p>
                            <p className="text-sm text-gray-600">Sipariş Tarihi: {new Date(returnRequest.orders.created_at).toLocaleDateString('tr-TR')}</p>
                        </div>

                        <div className="bg-white rounded-xl p-4 shadow-sm">
                            <div className="flex items-center mb-2">
                                <div className="w-8 h-8 bg-brand-green rounded-lg flex items-center justify-center mr-3">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a4 4 0 118 0v4m-4 6v6m-4-6h8" />
                                    </svg>
                                </div>
                                <h4 className="font-semibold text-gray-800">İade Tarihi</h4>
                            </div>
                            <p className="font-medium text-gray-900">{new Date(returnRequest.created_at).toLocaleDateString('tr-TR')}</p>
                            <p className="text-sm text-gray-600">{new Date(returnRequest.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                    </div>
                </div>

                {/* İade Nedeni - Çekmece */}
                <div className="p-6 border-b">
                    <div className="bg-orange-50 border border-orange-200 rounded-xl overflow-hidden">
                        {/* Çekmece Başlığı */}
                        <button
                            onClick={() => setIsReturnReasonExpanded(!isReturnReasonExpanded)}
                            className="w-full p-4 flex items-center justify-between hover:bg-orange-100 transition-colors"
                        >
                            <div className="flex items-center">
                                <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-orange-800 text-left">İade Nedeni</h4>
                                    <p className="text-orange-600 text-sm">Detayları görmek için tıklayın</p>
                                </div>
                            </div>
                            <svg
                                className={`w-5 h-5 text-orange-600 transition-transform duration-200 ${isReturnReasonExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {/* Çekmece İçeriği */}
                        {isReturnReasonExpanded && (
                            <div className="px-4 pb-4 border-t border-orange-200 bg-white">
                                <div className="pt-4">
                                    <p className="text-orange-700 text-lg leading-relaxed">{returnRequest.reason}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Kargo Takip Bilgisi (Eğer varsa) */}
                {returnRequest.status === 'shipped' && returnRequest.customer_tracking_number && (
                    <div className="p-6 border-b">
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                            <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-blue-800">Kargo Takip Numarası</h4>
                                    <p className="text-blue-700 font-mono text-lg">{returnRequest.customer_tracking_number}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* İletişim Talebi Uyarısı */}
                {(returnRequest.contact_admin_requested || returnRequest.admin_notes?.includes('İLETİŞİM TALEBİ')) && (
                    <div className="p-6 border-b">
                        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                            <div className="flex items-start">
                                <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-red-800 mb-2">🚨 MÜŞTERİ İLETİŞİM TALEBİ</h4>
                                    <p className="text-red-700 mb-3">Müşteri ödeme bilgilerini paylaşmak istemiyor. Lütfen müşteriyle iletişime geçin.</p>

                                    <div className="bg-white rounded-lg p-3 border border-red-200 mb-3">
                                        <h5 className="font-semibold text-gray-800 mb-2">Müşteri İletişim Bilgileri:</h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                            <div>
                                                <span className="text-gray-600">Email:</span>
                                                <p className="font-medium text-gray-800">{returnRequest.profiles?.email || 'Email bilgisi yok'}</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">Ad Soyad:</span>
                                                <p className="font-medium text-gray-800">{returnRequest.profiles?.full_name || 'Bilinmeyen Kullanıcı'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        <a
                                            href={`mailto:${returnRequest.profiles?.email}?subject=İade Ödeme İşlemi - Sipariş ${returnRequest.orders?.order_number}&body=Merhaba ${returnRequest.profiles?.full_name},%0D%0A%0D%0Aİade talebiniz onaylanmıştır. Ödeme işlemi için sizinle iletişime geçiyoruz.%0D%0A%0D%0ASipariş No: ${returnRequest.orders?.order_number}%0D%0Aİade ID: ${returnRequest.id}%0D%0A%0D%0ALütfen ödeme bilgilerinizi paylaşın.%0D%0A%0D%0ATeşekkürler.`}
                                            className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors text-center"
                                        >
                                            📧 Email Gönder
                                        </a>
                                        <button
                                            onClick={() => navigator.clipboard.writeText(returnRequest.profiles?.email)}
                                            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                                        >
                                            📋 Email Kopyala
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Kampanya Etkisi ve İade Hesaplama */}
                <div className="p-6 border-b">
                    <div className="bg-purple-50 border border-purple-200 rounded-xl overflow-hidden">
                        <div className="p-4">
                            <div className="flex items-center mb-3">
                                <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                                    <span className="text-white text-sm">💰</span>
                                </div>
                                <h4 className="font-semibold text-purple-800">Kampanya Etkisi ve İade Tutarı</h4>
                            </div>

                            {/* Kampanya Hesaplama Komponenti - Admin için */}
                            <CampaignRefundCalculation
                                order={{
                                    id: returnRequest.order_id,
                                    total_price: returnRequest.orders?.total_price,
                                    order_items: returnRequest.orders?.order_items || []
                                }}
                                selectedItems={returnRequest.orders?.order_items?.map(item => ({
                                    product_id: item.product_id,
                                    variant_id: item.variant_id,
                                    quantity: item.quantity,
                                    product_name: item.products?.name || item.product_name_at_purchase,
                                    variant_details: item.product_variants || item.variant_details_at_purchase,
                                    price: item.price_at_purchase,
                                    image_url: item.products?.main_image_url
                                })) || []}
                                onCalculationComplete={(data) => {
                                    console.log('Admin kampanya hesaplama tamamlandı:', data);
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Kargo Takip Bilgileri (Her durumda) */}
                {(
                    <div className="px-6 pt-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                            <h6 className="font-semibold text-blue-800 mb-3 flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Kargo Takip Bilgileri
                            </h6>
                            {/* Gönderici ve Alıcı Bilgileri */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* Gönderici */}
                                <div className="bg-blue-50 rounded-lg p-4">
                                    <div className="flex items-center mb-2">
                                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <h4 className="font-semibold text-blue-900">Gönderici: Müşteri</h4>
                                    </div>
                                    <div className="text-sm text-blue-800">
                                        <p className="font-medium">{returnRequest.orders?.shipping_address ? (() => {
                                            try {
                                                const address = JSON.parse(returnRequest.orders.shipping_address);
                                                return address.full_name || returnRequest.profiles?.full_name || 'Müşteri';
                                            } catch (e) {
                                                return returnRequest.profiles?.full_name || 'Müşteri';
                                            }
                                        })() : (returnRequest.profiles?.full_name || 'Müşteri')}</p>
                                        <p>{returnRequest.orders?.shipping_address ? (() => {
                                            try {
                                                const address = JSON.parse(returnRequest.orders.shipping_address);
                                                return (address.neighborhood ? address.neighborhood + ', ' : '') + (address.address_line || 'Adres bilgisi');
                                            } catch (e) {
                                                return 'Adres bilgisi';
                                            }
                                        })() : 'Adres bilgisi'}</p>
                                        <p>Tel: {returnRequest.orders?.shipping_address ? (() => {
                                            try {
                                                const address = JSON.parse(returnRequest.orders.shipping_address);
                                                return address.phone || returnRequest.profiles?.phone || '';
                                            } catch (e) {
                                                return returnRequest.profiles?.phone || '';
                                            }
                                        })() : (returnRequest.profiles?.phone || '')}</p>
                                    </div>
                                </div>

                                {/* Alıcı */}
                                <div className="bg-green-50 rounded-lg p-4">
                                    <div className="flex items-center mb-2">
                                        <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                        <h4 className="font-semibold text-green-900">Alıcı: Minela Kids</h4>
                                    </div>
                                    <div className="text-sm text-green-800">
                                        <p className="font-medium">Minela Kids</p>
                                        <p>İstanbul, Türkiye</p>
                                        <p>Tel: 05552609420</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Takip Numarası - En Önemli Bilgi */}
                                {(() => {
                                    const trackingInfo = formatTrackingCode(returnRequest?.kargonomi_tracking_code, returnRequest?.kargonomi_shipment_id);
                                    return (
                                        <div className="bg-white p-3 rounded-lg border border-blue-200">
                                            <div className="flex items-center mb-2">
                                                <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                </svg>
                                                <span className="text-sm font-semibold text-blue-700">Takip Numarası</span>
                                            </div>
                                            <p className={`font-mono font-bold text-lg px-3 py-2 rounded border ${trackingInfo.isReal
                                                ? 'text-blue-900 bg-blue-50'
                                                : 'text-orange-700 bg-orange-50'
                                                }`}>
                                                {trackingInfo.display}
                                            </p>
                                            <div className="flex space-x-2 mt-2">
                                                {trackingInfo.isReal && (
                                                    <button
                                                        onClick={() => navigator.clipboard.writeText(trackingInfo.display)}
                                                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                                                    >
                                                        📋 Kopyala
                                                    </button>
                                                )}
                                                {!trackingInfo.isReal && trackingInfo.shipmentId && (
                                                    <button
                                                        onClick={async () => {
                                                            const realCode = await fetchRealTrackingCode(trackingInfo.shipmentId);
                                                            if (realCode) {
                                                                // Burada gerçek takip numarasını güncelleyebiliriz
                                                                console.log('Gerçek takip numarası:', realCode);
                                                                // TODO: Veritabanını güncelle
                                                            }
                                                        }}
                                                        className="px-3 py-1 bg-orange-100 text-orange-700 rounded text-xs hover:bg-orange-200 transition-colors"
                                                    >
                                                        🔄 Gerçek Takip No Al
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Referans Numarası */}
                                {returnRequest?.kargonomi_reference_number && (
                                    <div className="bg-white p-3 rounded-lg border border-green-200">
                                        <div className="flex items-center mb-2">
                                            <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <span className="text-sm font-semibold text-green-700">Referans Numarası</span>
                                        </div>
                                        <p className="font-mono font-medium text-green-900 bg-green-50 px-3 py-2 rounded border">
                                            {returnRequest.kargonomi_reference_number}
                                        </p>
                                        <button
                                            onClick={() => navigator.clipboard.writeText(returnRequest.kargonomi_reference_number)}
                                            className="mt-2 px-3 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 transition-colors"
                                        >
                                            📋 Kopyala
                                        </button>
                                    </div>
                                )}

                                {/* Kargo Firması */}
                                {returnRequest?.kargonomi_shipping_provider && (
                                    <div className="bg-white p-3 rounded-lg border border-purple-200">
                                        <div className="flex items-center mb-2">
                                            <svg className="w-4 h-4 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            <span className="text-sm font-semibold text-purple-700">Kargo Firması</span>
                                        </div>
                                        <p className="font-medium text-purple-900 bg-purple-50 px-3 py-2 rounded border">
                                            {returnRequest.kargonomi_shipping_provider}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Takip URL'i varsa göster */}
                            {returnRequest?.tracking_url && (
                                <div className="mt-4 bg-white p-3 rounded-lg border border-blue-200">
                                    <div className="flex items-center mb-2">
                                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        <span className="text-sm font-semibold text-blue-700">Takip Linki</span>
                                    </div>
                                    <a
                                        href={returnRequest.tracking_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline break-all"
                                    >
                                        {returnRequest.tracking_url}
                                    </a>
                                    <button
                                        onClick={() => navigator.clipboard.writeText(returnRequest.tracking_url)}
                                        className="ml-2 px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                                    >
                                        📋 Kopyala
                                    </button>
                                </div>
                            )}

                            {/* Son Takip Güncellemesi */}
                            {returnRequest?.last_tracking_update && (
                                <div className="mt-3 text-xs text-gray-600">
                                    <span className="font-medium">Son Güncelleme:</span> {new Date(returnRequest.last_tracking_update).toLocaleString('tr-TR')}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* İşlem Alanı */}
                <div className="p-6">
                    {renderContent()}
                </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t flex-shrink-0">
                <button
                    onClick={closeModal}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-xl font-medium transition-colors"
                >
                    Kapat
                </button>
            </div>
        </div>
    );
};

function ReturnManagementPanel() {
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const { token } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);

    // Kargonomi API'den gerçek takip numarasını al
    const fetchRealTrackingCode = async (shipmentId) => {
        if (!shipmentId) return null;

        try {
            const response = await fetch(`/api/kargonomi/shipment/${shipmentId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.ok) {
                const data = await response.json();
                return data.data?.shipping_webservice_tracking_code || null;
            }
        } catch (error) {
            console.error('Takip numarası alınırken hata:', error);
        }
        return null;
    };

    // Takip numarasını formatla ve göster
    const formatTrackingCode = (trackingCode, shipmentId) => {
        // Eğer tracking code RETURN- ile başlıyorsa, bu yanlış veri
        if (trackingCode && trackingCode.startsWith('RETURN-')) {
            return {
                display: 'Takip numarası alınıyor...',
                isReal: false,
                shipmentId: shipmentId
            };
        }

        // Gerçek takip numarası
        if (trackingCode && trackingCode.length > 5) {
            return {
                display: trackingCode,
                isReal: true,
                shipmentId: shipmentId
            };
        }

        return {
            display: 'Takip numarası yok',
            isReal: false,
            shipmentId: null
        };
    };

    const fetchReturns = useCallback(async () => {
        if (!token) return;
        setLoading(true);

        debugLogger.info('İade yönetimi sayfası yükleniyor', {
            token: token ? 'Mevcut' : 'Yok',
            timestamp: new Date().toISOString()
        });

        try {
            debugLogger.info('API isteği gönderiliyor: /api/admin/returns/list');
            const data = await api.get('/api/admin/returns/list', { headers: { Authorization: `Bearer ${token}` } });

            debugLogger.info('İade verileri başarıyla alındı', {
                count: data?.length || 0,
                firstItem: data?.[0]?.id
            });

            setReturns(data);
        } catch (err) {
            debugLogger.error('İade verileri alınırken hata', {
                error: err.message,
                response: err.response?.data,
                status: err.response?.status
            });
            toast.error("İade talepleri getirilirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Otomatik takip kontrol fonksiyonları
    const handleStartTracking = async (returnId) => {
        setLoading(true);
        try {
            const response = await api.post(`/api/admin/returns/${returnId}/start-tracking`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.success) {
                toast.success('🚀 İade takibi başlatıldı!');
                fetchReturns(); // Listeyi yenile
            } else {
                toast.error(response.message || 'Takip başlatılamadı');
            }
        } catch (error) {
            console.error('Takip başlatma hatası:', error);
            toast.error(error.response?.data?.error || 'Takip başlatılırken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleStopTracking = async (returnId) => {
        setLoading(true);
        try {
            const response = await api.post(`/api/admin/returns/${returnId}/stop-tracking`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.success) {
                toast.success('🛑 İade takibi durduruldu');
                fetchReturns(); // Listeyi yenile
            } else {
                toast.error(response.message || 'Takip durdurulamadı');
            }
        } catch (error) {
            console.error('Takip durdurma hatası:', error);
            toast.error(error.response?.data?.error || 'Takip durdurulurken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleManualTrackingCheck = async (returnId) => {
        setLoading(true);
        try {
            const response = await api.post(`/api/admin/returns/${returnId}/manual-tracking-check`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.success) {
                toast.success('✅ Manuel takip kontrolü tamamlandı');
                fetchReturns(); // Listeyi yenile
            } else {
                toast.warning(response.message || 'Takip kontrolü tamamlandı');
                fetchReturns(); // Yine de listeyi yenile
            }
        } catch (error) {
            console.error('Manuel takip kontrol hatası:', error);
            toast.error(error.response?.data?.error || 'Manuel kontrol sırasında hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTrackingInfo = async (returnId) => {
        setLoading(true);
        try {
            const response = await api.post(`/api/admin/returns/${returnId}/update-tracking-info`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.success) {
                toast.success('✅ Takip bilgileri güncellendi');
                fetchReturns(); // Listeyi yenile
            } else {
                toast.warning(response.message || 'Takip bilgileri güncelleme tamamlandı');
                fetchReturns(); // Yine de listeyi yenile
            }
        } catch (error) {
            console.error('Takip bilgileri güncelleme hatası:', error);
            toast.error(error.response?.data?.error || 'Takip bilgileri güncellenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchReturns();
    }, [fetchReturns]);

    // Takip kodlarını düzelt
    const fixTrackingCodes = async () => {
        if (!token) return;

        const confirmFix = window.confirm(
            'Yanlış takip kodları düzeltilecek. Bu işlem biraz zaman alabilir. Devam etmek istiyor musunuz?'
        );

        if (!confirmFix) return;

        setLoading(true);
        try {
            const response = await api('/api/fix-tracking/fix-tracking-codes', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            toast.success(`Takip kodları düzeltildi! ${response.totalProcessed} adet işlem yapıldı.`);

            // İade listesini yenile
            await fetchReturns();
        } catch (err) {
            toast.error('Takip kodları düzeltilirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (returnRequest) => {
        setSelectedReturn(returnRequest);
        setIsModalOpen(true);
    };

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const getStatusText = (status) => {
        switch (status) {
            case 'pending_approval':
                return 'Onay Bekliyor';
            case 'approved':
                return 'Müşterinin kargoya vermesi bekleniyor';
            case 'rejected':
                return 'Reddedildi';
            case 'shipping_label_created':
                return 'Kargo etiketi hazır';
            case 'customer_shipped':
                return 'Müşteri kargoya verdi';
            case 'in_transit':
                return 'Yolda';
            case 'received':
                return 'Mağazada alındı';
            case 'under_review':
                return 'İncelemede';
            case 'review_completed':
                return 'İnceleme tamamlandı';
            case 'refund_approved':
                return 'İade onaylandı';
            case 'refund_completed':
                return 'İade tamamlandı';
            case 'rejected_reshipping':
                return 'Yeniden gönderim reddedildi';
            case 'completed':
                return 'Tamamlandı';
            case 'cancelled':
                return 'İptal edildi';
            default:
                return status;
        }
    };

    return (
        <div className="p-2 sm:p-4 lg:p-6 bg-gray-50 min-h-screen">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-brand-green to-brand-green-light p-4 sm:p-6 text-white">
                    <h2 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">İade Yönetimi</h2>
                    <p className="opacity-90 text-sm sm:text-base">İade taleplerini görüntüleyin ve yönetin</p>
                </div>

                <div className="p-4 sm:p-6">
                    {/* Desktop Tablo - Sadece büyük ekranlarda */}
                    <div className="hidden lg:block bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Müşteri</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Sipariş No</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Takip No</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Talep Tarihi</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Durum</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {loading ? (
                                        <tr><td colSpan="6" className="text-center p-10">Yükleniyor...</td></tr>
                                    ) : returns.map(ret => {
                                        const statusInfo = statusConfig[ret.status] || { text: ret.status, color: 'gray' };
                                        const StatusIcon = statusInfo.icon || PencilSquareIcon;
                                        return (
                                            <tr key={ret.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <p className="text-sm font-medium text-gray-900">{ret.profiles?.full_name || 'İsimsiz'}</p>
                                                    <p className="text-xs text-gray-500">{ret.profiles?.email || 'E-postasız'}</p>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">MINELA-{ret.orders?.order_number}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    {(() => {
                                                        const trackingInfo = formatTrackingCode(ret.kargonomi_tracking_code || ret.kargonomi_reference_number, ret.kargonomi_shipment_id);
                                                        return (
                                                            <div className="flex items-center space-x-2">
                                                                <span className={`font-mono px-2 py-1 rounded text-xs font-medium ${trackingInfo.isReal
                                                                    ? 'text-blue-600 bg-blue-50'
                                                                    : 'text-orange-600 bg-orange-50'
                                                                    }`}>
                                                                    {trackingInfo.display}
                                                                </span>
                                                                {trackingInfo.isReal && (
                                                                    <button
                                                                        onClick={() => navigator.clipboard.writeText(trackingInfo.display)}
                                                                        className="text-blue-500 hover:text-blue-700 text-xs"
                                                                        title="Kopyala"
                                                                    >
                                                                        📋
                                                                    </button>
                                                                )}
                                                                {!trackingInfo.isReal && trackingInfo.shipmentId && (
                                                                    <button
                                                                        onClick={async () => {
                                                                            const realCode = await fetchRealTrackingCode(trackingInfo.shipmentId);
                                                                            if (realCode) {
                                                                                console.log('Gerçek takip numarası:', realCode);
                                                                            }
                                                                        }}
                                                                        className="text-orange-500 hover:text-orange-700 text-xs"
                                                                        title="Gerçek takip numarasını al"
                                                                    >
                                                                        🔄
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(ret.created_at)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
                                                        <StatusIcon className={`-ml-0.5 mr-1.5 h-4 w-4 text-${statusInfo.color}-600`} />
                                                        {statusInfo.text}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button onClick={() => handleOpenModal(ret)} className="p-2 rounded-full text-blue-600 hover:bg-blue-100 hover:text-blue-800" title="İşlem Yap">
                                                        <PencilSquareIcon className="h-5 w-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Mobil Kart Görünümü - Küçük ve orta ekranlarda */}
                    <div className="lg:hidden space-y-4">
                        {loading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green mx-auto"></div>
                                <p className="mt-2 text-gray-600">İade talepleri yükleniyor...</p>
                            </div>
                        ) : returns.map(ret => {
                            const statusInfo = statusConfig[ret.status] || { text: ret.status, color: 'gray' };
                            const StatusIcon = statusInfo.icon || PencilSquareIcon;
                            return (
                                <div key={ret.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-gray-900 text-lg">
                                                {ret.profiles?.full_name || 'İsimsiz Kullanıcı'}
                                            </h3>
                                            <p className="text-gray-600 text-sm mt-1">{ret.profiles?.email || 'E-postasız'}</p>
                                        </div>
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
                                            <StatusIcon className={`-ml-0.5 mr-1 h-3 w-3 text-${statusInfo.color}-600`} />
                                            {statusInfo.text}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-sm">
                                        <div>
                                            <span className="text-gray-500">Sipariş No:</span>
                                            <p className="font-medium">MINELA-{ret.orders?.order_number}</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Talep Tarihi:</span>
                                            <p className="font-medium">{formatDate(ret.created_at)}</p>
                                        </div>
                                    </div>

                                    {/* Takip Numarası - Mobil Görünüm */}
                                    <div className="mb-3">
                                        <span className="text-gray-500 text-sm">Takip Numarası:</span>
                                        {(() => {
                                            const trackingInfo = formatTrackingCode(ret.kargonomi_tracking_code, ret.kargonomi_shipment_id);
                                            return (
                                                <div className="flex items-center space-x-2 mt-1">
                                                    <span className={`font-mono px-2 py-1 rounded text-xs font-medium ${trackingInfo.isReal
                                                        ? 'text-blue-600 bg-blue-50'
                                                        : 'text-orange-600 bg-orange-50'
                                                        }`}>
                                                        {trackingInfo.display}
                                                    </span>
                                                    {trackingInfo.isReal && (
                                                        <button
                                                            onClick={() => navigator.clipboard.writeText(trackingInfo.display)}
                                                            className="text-blue-500 hover:text-blue-700 text-xs"
                                                            title="Kopyala"
                                                        >
                                                            📋
                                                        </button>
                                                    )}
                                                    {!trackingInfo.isReal && trackingInfo.shipmentId && (
                                                        <button
                                                            onClick={async () => {
                                                                const realCode = await fetchRealTrackingCode(trackingInfo.shipmentId);
                                                                if (realCode) {
                                                                    console.log('Gerçek takip numarası:', realCode);
                                                                }
                                                            }}
                                                            className="text-orange-500 hover:text-orange-700 text-xs"
                                                            title="Gerçek takip numarasını al"
                                                        >
                                                            🔄
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleOpenModal(ret)}
                                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                                        >
                                            <PencilSquareIcon className="h-4 w-4 mr-1" />
                                            İşlem Yap
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {isModalOpen && selectedReturn && (
                        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                            <ReturnActionModal
                                returnRequest={selectedReturn}
                                closeModal={() => setIsModalOpen(false)}
                                onActionSuccess={fetchReturns}
                                onUpdateTrackingInfo={handleUpdateTrackingInfo}
                                fetchRealTrackingCode={fetchRealTrackingCode}
                                formatTrackingCode={formatTrackingCode}
                            />
                        </Modal>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ReturnManagementPanel;
